const { message } = require('laravel-mix/src/Log');
const notiflix = require('notiflix');
const HttpRequest = require('../../../../js/HttpRequest');


class TpRegister {
    /**
     * @returns {TpRegister} Instance of.
     */
    constructor() {
        this.form = {};
        this._initRegistrationForm();
    }

    /**
     * Initializes the registration form with inputs and buttons.
     *
     * @returns {void}
     */
    async _initRegistrationForm() {
        this.form = {
            nameInput: document.getElementById('fname'),
            emailInput: document.getElementById('femail'),
            passInput: document.getElementById('fpass'),
            cpassInput: document.getElementById('fcpass'),
            touInput: document.getElementById('tou'),
            submitButton: document.getElementById('fsub')
        };

        // make sure all inputs are present.
        if (
            !this.form.nameInput ||
            !this.form.emailInput ||
            !this.form.passInput ||
            !this.form.cpassInput ||
            !this.form.touInput ||
            !this.form.submitButton
        ) {
            notiflix.Notify.failure(__lang.FORM_BROKEN_ERROR, this._getNotiflixSetup());

            return;
        };

        // submit form.
        this.form.submitButton.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            await this._handleFormSubmit();
        });

        this.form.submitButton.addEventListener('touchstart', async (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            await this._handleFormSubmit();
        });
    }

    /**
     * Handles the registration form submission.
     * Validates the input fields, displays notificlix notifications on errors and sends a registration request to the server.
     */
    async _handleFormSubmit() {
        const name = this.form.nameInput.value.trim();
        const email = this.form.emailInput.value.trim().toLowerCase();
        const pass = this.form.passInput.value.trim();
        const cpass = this.form.cpassInput.value.trim();
        const tou = this.form.touInput.checked ? true : false;
        const preloader = document.querySelector('.preloader');

        // validate name.
        if (name.length < 1) {
            notiflix.Notify.failure(__lang.NAME_LENGTH_ERROR, this._getNotiflixSetup());

            return;
        }

        // validate email format.
        if (!this._validateEmailAddress(email)) {
            notiflix.Notify.failure(__lang.INVALID_EMAIL_ERROR, this._getNotiflixSetup());

            return;
        }

        // validate password length.
        if (pass.length < 6) {
            notiflix.Notify.failure(__lang.PASSWORD_LENGTH_ERROR, this._getNotiflixSetup());

            return;
        }

        // check confirm password.
        if (pass !== cpass) {
            notiflix.Notify.failure(__lang.CONFIRM_PASSWORD_ERROR, this._getNotiflixSetup());

            return;
        }

        // check terms of use acceptance.
        if (!tou) {
            notiflix.Notify.failure(__lang.TOU_ERROR, this._getNotiflixSetup());

            return;
        }

        // send registration request.
        const payload = {
            name: name,
            email: email,
            password: pass,
            cpassword: cpass,
            tou: tou
        };

        if (preloader) {
            preloader.removeAttribute('style');
            preloader.style.opacity = '0.5';
        }

        await this._registerUser(payload)
            .then(async (response) => {
                if (!response || typeof response !== 'object' || !response.ok) {
                    if (message && typeof response.message === 'string') {
                        if (response.message.includes('CSRF')) {
                            window.location.reload();

                            return;
                        }
                    }

                    notiflix.Notify.failure(__lang.ACCOUNT_REGISTRATION_ERROR, this._getNotiflixSetup());

                    return;
                }

                // send to controller for registration.
                const payload = {
                    name: response.name ?? null,
                    email: response.email ?? null,
                    password: response.password ?? null,
                    token: response.token ?? null,
                    expires: response.expires ?? null
                };

                const data = {
                    method: 'PUT',
                    url: '/register/store',
                    payload: payload,
                    csrfMode: 'sanctum-cookie',
                    autoRefreshCsrf: true
                };

                await HttpRequest.request(data)
                    .then(r => {
                        const statusCode = r.status;

                        if (statusCode !== 201 && statusCode !== 200) {
                            notiflix.Notify.failure(__lang.ACCOUNT_EMAIL_TAKEN_ERROR, this._getNotiflixSetup());
                            preloader.removeAttribute('style');
                            preloader.style.opacity = '0';
                            preloader.style.display = 'none';

                            return;
                        }

                        // reset form.
                        this.form.nameInput.value = '';
                        this.form.emailInput.value = '';
                        this.form.passInput.value = '';
                        this.form.cpassInput.value = '';
                        this.form.touInput.checked = false;

                        preloader.removeAttribute('style');
                        preloader.style.opacity = '0';
                        preloader.style.display = 'none';

                        notiflix.Notify.success(__lang.ACCOUNT_REGISTRATION_SUCCESS, () => { }, this._getNotiflixSetup(), {
                            clickToClose: false,
                            clickToCloseDelay: 5000,
                            closeButton: true,
                            position: 'center-bottom',
                            width: '420px'
                        });
                    })
                    .catch(e => {
                        notiflix.Notify.failure(__lang.ACCOUNT_REGISTRATION_ERROR, this._getNotiflixSetup());

                        preloader.removeAttribute('style');
                        preloader.style.opacity = '0';
                        preloader.style.display = 'none';
                    });
            })
            .catch((error) => {
                notiflix.Notify.failure(__lang.ACCOUNT_REGISTRATION_ERROR, this._getNotiflixSetup());

                if (error.message) {
                    console.log(error);
                }

                preloader.removeAttribute('style');
                preloader.style.opacity = '0';
                preloader.style.display = 'none';
            });
    }

    /**
     * Sends a registration request to the server.
     * Returns a promise that resolves to an object with ok and message properties.
     * If the registration request fails, ok is set to false and message is set to the error message.
     * If the registration request succeeds, ok is set to true and message is set to 'Registration successful.'.
     *
     * @param {Object} payload - Registration request payload.
     * @returns {Promise<Object>} - Promise that resolves to an object with ok and message properties.
     */
    async _registerUser(payload) {
        const csrfToken = document
            .querySelector('meta[name="csrf-token"]')
            .getAttribute('content');

        const response = await fetch('/register', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'Accept': 'application/json'
            },
            body: JSON.stringify(payload),
            credentials: 'same-origin'
        });

        let data = {
            ok: false,
            message: null
        };

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));

            data.ok = false;
            data.message = error.message || 'Registration failed. Invalid request response.';

            return data;
        }

        data = await response.json();
        data.ok = true;

        return data;
    }

    /**
     * Validate an email address.
     *
     * This function will return true if the email address is valid, and false otherwise.
     *
     * The validation is performed according to the following rules:
     * - The email address must be a string.
     * - The email address must not be empty.
     * - The email address must not exceed 254 characters in length.
     * - The email address must match the following regex: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
     * - The local part of the email address must not contain consecutive dots.
     * - The domain part of the email address must not start or end with a dot.
     *
     * @param {string} email The email address to validate.
     * @return {boolean} True if the email address is valid, false otherwise.
     */
    _validateEmailAddress(email) {
        if (typeof email !== 'string') return false;

        // Trim whitespace
        email = email.trim();

        if (email.length === 0 || email.length > 254) return false;

        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

        if (!regex.test(email)) return false;

        const [local, domain] = email.split('@');

        if (local.includes('..') || domain.includes('..')) return false;
        if (domain.startsWith('.') || domain.endsWith('.')) return false;

        return true;
    }

    /**
     * Returns the setup object for Notiflix.
     *
     * @return {Object}
     * @property {string} position - The position of the Notiflix.
     * @property {string} width - The width of the Notiflix.
     * @property {boolean} showOnlyTheLastOne - Whether to show only the last notification.
     */
    _getNotiflixSetup() {
        return {
            position: 'center-bottom',
            width: '320px',
            showOnlyTheLastOne: true
        };
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new TpRegister();
});
